import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import Loader from '../../../../components/CoreComponents/Loader';
import ViewTable from '../../../../components/CoreComponents/ViewTable';
import { URLs } from '../../../../Constants/Consts';
import { fetchApi } from '../../../../Helpers/Functions';
import { FileDrawingType } from '../../../../types/types';
import { RootState } from '../../../../store/store';

const MapsDrawingsQueryKey = "MapsDrawings";

interface FileDrawingTypeExtended extends FileDrawingType {
    loadForProject?: boolean;
}

export default function MapsDrawings() {
    const { projectNumber } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);

    // Fetch drawings for the user's assigned project
    const { data: projectMaps, isLoading } = useQuery<Array<{
        url: string;
        label: string;
        id: string;
        filename: string;
    }>>({
        queryKey: [MapsDrawingsQueryKey, projectNumber],
        queryFn: async () => {
            const drawings = await fetchApi(URLs.getDrawingsUrl(Number(projectNumber)))
                .then(res => res.json()) as Array<FileDrawingTypeExtended>;
            
            const drawingsData = await Promise.all(drawings.map(async ({ 
                projectNumber: projNum, 
                drawingFilename, 
                drawingDisplayName, 
                drawingUuid,
            }) => {
                const fileblob = await (await fetchApi(URLs.getFileBlobUrl2(Number(projNum), String(drawingFilename)))).blob();

                return {
                    url: URL.createObjectURL(fileblob),
                    label: drawingDisplayName as string,
                    id: String(drawingUuid),
                    filename: drawingFilename,
                };
            }));
            return drawingsData;
        },
        staleTime: 0,
        enabled: !!projectNumber && projectNumber !== 0,
    });

    if (!projectNumber || projectNumber === 0) {
        return (
            <div className="font-bold text-center mt-8">
                <p>No project assigned to your account.</p>
            </div>
        );
    }

    return (
        <>
            {isLoading ? (
                <Loader loaders={{ isProjectMapsLoading: true }} />
            ) : (
                <div className='font-bold'>
                    <div className='flex justify-between items-center mb-4'>
                        <span>Project Number: {projectNumber}</span>
                        <span className='text-sm font-normal text-gray-600 dark:text-gray-400'>
                            Total Maps: {projectMaps?.length || 0}
                        </span>
                    </div>
                    
                    <ViewTable
                        columns={[
                            {
                                Header: 'Drawing Name',
                                accessor: 'label',
                            },
                            {
                                Header: 'Map',
                                accessor: 'url',
                                Cell: ({ value }) => (
                                    <img 
                                        src={value} 
                                        alt="Map preview"
                                        className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                                        title='View Map Preview'
                                    />
                                )
                            },
                        ]}
                        data={projectMaps || []}
                    />
                </div>
            )}
        </>
    );
}
